"use client";
import { ChangeEvent, useEffect, useState } from "react";
import { Button, Modal, Form, Row, Col, Stack } from "react-bootstrap";
import "../Calendar/Calendar.css";
import CalendarBackend from "../Calendar/utils/CalendarBackend";
import ModalIconTitle from "../ModalIconTitle";
import { CgCalendarToday } from "react-icons/cg";
import TagSelector from "../ReactSelect";
import moment from "moment";
import common from "../../utils/common";
import { AxiosResponse } from "axios";
import {
  CalendarApi,
  EventClickArg,
  DateSelectArg,
  EventContentArg,
} from "@fullcalendar/core/index.js";
import { CalendarData } from "../../class/frontend.schema";
import { MultiValue, SingleValue } from "react-select";
import TextInput from "../../components/Modal/Input/TextInput";
import DateInput from "../../components/Modal//Input/DateInput";
import TextAreaInput from "../../components/Modal//Input/TextAreaInput";
import { useModalWidthClass } from "../../hooks/useModalWidth";
import { cal_calendar, cal_record, cal_place } from "../../class/prisma/db";
import { UpdateRecordRequest } from "../../class/record.schema";
import { CalendarViewObject } from "../../class/calendar.schema";
import toast from "react-hot-toast";
import { SystemMsg } from "../../utils/SystemMsg";
import { IoLocationOutline } from "react-icons/io5";
import MapModal from "./MapModal";
import { useI18n } from "../../i18n";

type EventModalProp = {
  toast: typeof toast;
  mode: string;
  show: boolean;
  onSaveBtnClick: (
    resp: AxiosResponse,
    api?: CalendarApi,
    mode?: string,
    data?: CalendarData | cal_record,
  ) => void;
  onClose: () => void;
  calendars?: CalendarViewObject[];
  places?: cal_place[];
  isCalendar: boolean;
  data:
    | DateSelectArg
    | EventClickArg
    | EventContentArg
    | cal_record
    | cal_record[]
    | undefined;
};

type EventModalValidationResult = {
  title: boolean;
  startDate: boolean;
  endDate: boolean;
  calendarIds: boolean;
};

export default function EventModal({
  toast,
  mode,
  show,
  onSaveBtnClick,
  onClose,
  data,
  calendars,
  places,
  isCalendar = true,
}: EventModalProp) {
  const { t } = useI18n();
  const [modeState, setModeState] = useState<"add" | "edit">(`add`);

  const [id, setId] = useState(-1);
  const [title, setTitle] = useState("");
  const [startDateChanged, setStartDateChanged] = useState<Boolean>(false);
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const [startDateStr, setStartDateStr] = useState<string | undefined>();
  const [endDateStr, setEndDateStr] = useState<string | undefined>();

  const [description, setDescription] = useState("");
  const [placeId, setPlaceId] = useState(-1);
  const [calendarIds, setCalendarIds] = useState<any[]>([]);

  const [calendarOptions, setCalendarOptions] = useState<any[]>([]);
  const [placeOptions, setPlaceOptions] = useState<any[]>([]);

  const [endDateInvalid, setEndDateInvalid] = useState(false);

  const [startDateValidationMsg] = useState(
    SystemMsg.Common.pleaseEnterAValidX(`Start Date`),
  );
  const [endDateValidationMsg, setEndDateValidationMsg] = useState(
    SystemMsg.Common.pleaseEnterAValidX(`End Date`),
  );

  const [validationResult, setValidationResult] =
    useState<EventModalValidationResult>();

  const [modalTitle, setModalTitle] = useState(t("event.add"));

  const [showMapModal, setShowMapModel] = useState(false);
  const [placeName, setPlaceName] = useState(``);
  const [address, setAddress] = useState(``);

  const dialogClassName = useModalWidthClass();

  useEffect(() => {
    if (Array.isArray(calendars)) {
      const options = [];
      for (const item of calendars as unknown as cal_calendar[]) {
        options.push({
          value: item.id.toString(),
          label: item.name,
          color: item.color,
        });
      }
      setCalendarOptions(options);
    }
  }, [calendars]);

  useEffect(() => {
    if (!show) {
      return;
    }

    const isEditOrView = mode === "edit" || mode === "view";
    if (isEditOrView) {
      if (!data || Object.keys(data).length === 0) {
        toast.error(t("event.unableToGet"));
        console.error(`Unable to get event data!`);
        onHide();
        return;
      }
    }
    if (isCalendar) {
      convertDataFromCalendar(mode, data);
    } else {
      convertDataFromGrid(mode, data);
    }

    const modeLower = mode.toString().toLowerCase();
    if (modeLower === `add`) {
      setModeState(`add`);
    }
    if (modeLower === `edit`) {
      setModeState(`edit`);
    }
  }, [show, mode, data, isCalendar]);

  useEffect(() => {
    if (modeState === `add`) {
      setModalTitle(t("event.add"));
    }
    if (modeState === `edit`) {
      setModalTitle(t("event.edit"));
    }
  }, [modeState]);

  useEffect(() => {
    if (!startDateChanged || !startDate || !moment(startDate).isValid()) {
      return;
    }

    const newEndDate = moment(startDate).add(1, "hour").toDate();
    setEndDate(newEndDate);
    setEndDateStr(common.getDateTimeLocalValue(newEndDate));
    setStartDateChanged(false);
  }, [startDateChanged, startDate]);

  useEffect(() => {
    if (
      !calendarIds ||
      !Array.isArray(calendarIds) ||
      calendarIds.length === 0
    ) {
      setPlaceOptions([]);
      return;
    }

    console.log(184, places);
    if (!places || !Array.isArray(places)) {
      setPlaceOptions([]);
      return;
    }

    const options = [];
    for (const item of places as cal_place[]) {
      if (!calendarIds.includes(item.calendar_id)) {
        continue;
      }
      options.push({
        value: item.id.toString(),
        label: item.name,
      });
    }
    setPlaceOptions(options);
  }, [calendarIds]);

  const convertDataFromCalendar = (mode: string, data: any) => {
    const isEditOrView = mode === "edit";
    if (isEditOrView) {
      const eventArgs = data as EventClickArg;
      setId(common.strToNumber(eventArgs.event.id));
      setTitle(eventArgs.event?.title ?? "");
      setStartDate(eventArgs.event?.start);
      setStartDateStr(
        common.getDateTimeLocalValue(eventArgs.event?.start ?? ""),
      );

      setEndDate(eventArgs.event?.end);
      setEndDateStr(common.getDateTimeLocalValue(eventArgs.event?.end ?? ""));
      setDescription(eventArgs.event?.extendedProps?.description ?? "");
      const calendar_id = eventArgs.event?.extendedProps?.calendarIds;
      if (
        calendar_id &&
        !Array.isArray(calendar_id) &&
        common.strToNumber(calendar_id.toString()) > 0
      ) {
        setCalendarIds([common.strToNumber(calendar_id.toString())]);
      } else {
        setCalendarIds(calendar_id);
      }
      setPlaceId(common.strToNumber(eventArgs?.event?.extendedProps?.placeId));
    } else {
      const selectArgs = data as DateSelectArg;
      const startDateStr =
        moment(selectArgs?.start).format("YYYY-MMM-DD") +
        ` ${moment().format(`HH:mm`)}`;
      addModeData(startDateStr);
    }
  };

  const convertDataFromGrid = (mode: string, data: any) => {
    const isEditOrView = mode === "edit";
    if (isEditOrView) {
      const record = data as cal_record;
      setId(record.id);
      setTitle(record.name);
      if (moment(record.start_date).isValid()) {
        setStartDate(moment(record.start_date).toDate());
        setStartDateStr(
          common.getDateTimeLocalValue(moment(record.start_date).toDate()),
        );
      }
      if (moment(record.end_date).isValid()) {
        setEndDate(moment(record.end_date).toDate());
        setEndDateStr(
          common.getDateTimeLocalValue(moment(record.end_date).toDate()),
        );
      }
      setDescription(record.description);
      if (record.calendar_id !== null && record.calendar_id !== undefined) {
        setCalendarIds([record.calendar_id]);
      }
      if (record.place_id !== undefined && record.place_id !== null) {
        setPlaceId(record.place_id);
      }
    } else {
      addModeData(new Date());
    }
  };

  const addModeData = (
    date: Date | string,
    endDate: Date | string | undefined = undefined,
  ) => {
    const hasEndDate = endDate !== undefined && endDate !== null;
    const timeStr = moment().format(`HH:mm:ss`);
    const start = hasEndDate
      ? moment(date).toDate()
      : moment(moment(date).format(`YYYY-MM-DD`) + ` ${timeStr}`).toDate();
    const end = hasEndDate
      ? moment(endDate).toDate()
      : moment(start).add(1, "hour").toDate();
    setStartDate(start);
    setEndDate(end);
    setStartDateStr(common.getDateTimeLocalValue(start));
    setEndDateStr(common.getDateTimeLocalValue(end));
  };

  const saveEventClick = async () => {
    if (!validateForm()) {
      return;
    }
    let calendarUi: CalendarApi | undefined = undefined;
    if (isCalendar) {
      calendarUi = (data as DateSelectArg | EventClickArg).view.calendar;
      calendarUi.unselect();
    }
    onHide();
    if (modeState === `add`) {
      await addEventAction(
        modeState,
        calendarUi,
        getSendData(),
        getCallbackData(isCalendar, id),
      );
    }
    if (modeState === `edit`) {
      await editEventAction(
        id,
        modeState,
        calendarUi,
        getSendData(),
        getCallbackData(isCalendar, id),
      );
    }
  };

  const addEventAction = async (
    mode: string,
    calendarApi: CalendarApi | undefined,
    sendData: UpdateRecordRequest,
    callbackData: CalendarData | cal_record,
  ) => {
    const succFunc = (resp: AxiosResponse) => {
      // eventObj.id = id;
      if (typeof onSaveBtnClick === "function") {
        onSaveBtnClick(resp, calendarApi, mode, callbackData);
      }
    };
    const errFunc = (resp: AxiosResponse | string) => {
      if (typeof onSaveBtnClick === "function") {
        onSaveBtnClick(resp as AxiosResponse, calendarApi, mode, callbackData);
      }
    };
    return CalendarBackend.addRecord(
      sendData.calendar_ids ?? [],
      sendData,
      succFunc,
      errFunc,
    );
  };

  const editEventAction = async (
    id: number,
    mode: string,
    calendarApi: CalendarApi | undefined,
    sendData: UpdateRecordRequest,
    callbackData: CalendarData | cal_record,
  ) => {
    const succFunc = (resp: AxiosResponse) => {
      if (typeof onSaveBtnClick === "function") {
        onSaveBtnClick(resp, calendarApi, mode, callbackData);
      }
    };
    const errFunc = (resp: AxiosResponse | string) => {
      if (typeof onSaveBtnClick === "function") {
        onSaveBtnClick(resp as AxiosResponse, calendarApi, mode, callbackData);
      }
    };
    return CalendarBackend.editRecord(id, sendData, succFunc, errFunc);
  };

  const getSendData = (): UpdateRecordRequest => {
    return {
      name: title,
      start_date: new Date(startDate as Date),
      end_date: new Date(endDate as Date),
      description: description,
      place_id: placeId,
      calendar_ids: calendarIds,
      is_deleted: false,
    } as UpdateRecordRequest;
  };

  const getCallbackData = (
    isCalendar: boolean,
    id: number,
  ): CalendarData | cal_record => {
    if (isCalendar) {
      return {
        id: id.toString(),
        title: title,
        start: new Date(startDate as Date).toISOString(),
        end: new Date(endDate as Date).toISOString(),
        startDate: startDate as Date,
        endDate: endDate as Date,
        description: description,
        placeId: placeId,
        calendarIds: calendarIds,
        tag: "event",
      } as CalendarData;
    } else {
      return {
        id: id,
        name: title,
        start_date: new Date(startDate as Date),
        end_date: new Date(endDate as Date),
        description: description,
        place_id: placeId,
        calendar_id: calendarIds[0],
        is_deleted: false,
      } as cal_record;
    }
  };

  const copyEventClick = async () => {
    setModeState(`add`);
    toast.success(t("event.copied", { id: id }));
    setStartDate(moment().toDate());
    setEndDate(moment().add(1, `hour`).toDate());
  };

  const onHide = () => {
    setId(-1);
    setTitle("");
    setStartDate(new Date());
    setEndDate(new Date());
    setDescription("");
    setPlaceId(-1);
    setCalendarIds([]);
    setValidationResult(undefined);
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const validateForm = () => {
    const title_valid = !title || title.toString().length === 0 ? false : true;
    const startDate_valid =
      !startDate ||
      startDate.toString().length === 0 ||
      !moment(startDate).isValid()
        ? false
        : true;

    const calendarIds_valid =
      calendarIds == null ||
      !Array.isArray(calendarIds) ||
      calendarIds.length === 0
        ? false
        : true;
    let endDate_valid = true;
    if (
      !endDate ||
      endDate.toString().length === 0 ||
      !moment(endDate).isValid()
    ) {
      endDate_valid = false;
    }

    if (moment(startDate) >= moment(endDate)) {
      endDate_valid = false;
      setEndDateValidationMsg(`End Date must be greater than Start Date!`);
    }

    const result: EventModalValidationResult = {
      title: title_valid,
      startDate: startDate_valid,
      endDate: endDate_valid,
      calendarIds: calendarIds_valid,
    };
    setValidationResult(result);
    const valid =
      title_valid && startDate_valid && endDate_valid && calendarIds_valid;
    return valid;
  };

  const getSelectedOptionsById = (
    options: any[],
    id: number | number[],
    isSingle = false,
  ) => {
    if (!id || id.toString().length === 0) {
      return [];
    }

    if (isSingle && Number.isNaN(Number(id))) {
      return [];
    }

    if (!options || !Array.isArray(options) || options.length === 0) {
      return [];
    }

    if (Array.isArray(id)) {
      return options.filter((x) =>
        id.map((x) => x.toString()).includes(x.value),
      );
    }

    const findResults = !isSingle
      ? options.filter((x) => x.value.toString() === id.toString())
      : options.find((x) => x.value.toString() === id.toString());
    return findResults;
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        backdrop="static"
        dialogClassName={dialogClassName}
        centered
      >
        <Modal.Header style={{ border: 0 }} closeButton>
          <Modal.Title>
            <ModalIconTitle
              title={modalTitle}
              iconElement={<CgCalendarToday />}
            />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <TextInput
              label={t("fields.title")}
              value={title}
              placeholder={t("fields.title")}
              setValueFunc={(val: string | number) => {
                setTitle(val as string);
              }}
              invalid={
                validationResult !== undefined &&
                validationResult?.title === false
              }
            />
          </Row>
          <Form.Group className="mb-3" controlId="eventModal.CalendarId">
            <TagSelector
              isMulti={true}
              options={calendarOptions}
              defaultValue={getSelectedOptionsById(
                calendarOptions,
                calendarIds,
              )}
              onChange={(value: MultiValue<any> | SingleValue<any>) => {
                if (!value || !Array.isArray(value) || value.length === 0) {
                  setCalendarIds([]);
                  return;
                }
                setCalendarIds(value.map((x) => Number(x.value) as number));
                if (validationResult !== undefined) {
                  validationResult.calendarIds = true;
                }
              }}
              isInvalid={
                validationResult !== undefined &&
                validationResult?.calendarIds === false
              }
              placeholder="Select Calendars"
            />
          </Form.Group>
          <Row className="mb-3">
            <DateInput
              value={startDateStr}
              placeholder={t("fields.startDate")}
              setValueFunc={(val: string | number) => {
                setStartDateChanged(true);
                setStartDateStr(val as string);
                setStartDate(new Date(val));
              }}
              invalid={
                !startDate ||
                !moment(startDate).isValid() ||
                (validationResult !== undefined &&
                  validationResult?.startDate === false)
              }
              invalidMsg={startDateValidationMsg}
            />
            <DateInput
              value={endDateStr}
              placeholder={t("fields.endDate")}
              setValueFunc={(val: string | number) => {
                setEndDateStr(val as string);
                if (!moment(new Date(val)).isValid()) {
                  setEndDateInvalid(true);
                  setEndDateValidationMsg("Please enter a valid End Date!");
                  return;
                }
                setEndDateValidationMsg("Please enter the End Date!");
                setEndDateInvalid(false);
                setEndDate(new Date(val));
                if (validationResult !== undefined) {
                  validationResult.endDate = true;
                }
              }}
              invalid={
                endDateInvalid ||
                (validationResult !== undefined &&
                  validationResult?.endDate === false)
              }
              invalidMsg={endDateValidationMsg}
            />
          </Row>
          <TextAreaInput
            placeholder={t("fields.description")}
            value={description}
            setValueFunc={(value: string | number) => {
              setDescription(value as string);
            }}
          />

          <Row className="mb-3">
            <Stack direction="horizontal" gap={2}>
              <Form.Group as={Col} controlId="eventModal.placeId">
                <Form.Label>{t("fields.place")}</Form.Label>
                <Form.Select
                  value={placeId}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                    if (!event || event.target.value.toString().length === 0) {
                      return;
                    }
                    setPlaceId(Number(event.target.value));
                  }}
                >
                  <option>Please select</option>
                  <>
                    {placeOptions.map((x) => (
                      <option value={x.value}>{x.label}</option>
                    ))}
                  </>
                </Form.Select>
              </Form.Group>
              <div>
                <Button
                  variant="secondary"
                  style={{ marginTop: "30px" }}
                  onClick={() => {
                    const find = places?.find((p) => p.id === placeId);
                    if (!find) return;
                    setPlaceName(find.name);
                    setAddress(find.address);
                    setShowMapModel(true);
                  }}
                >
                  <IoLocationOutline />
                </Button>
              </div>
            </Stack>
          </Row>
        </Modal.Body>
        <Modal.Footer style={{ justifyContent: "flex-start" }}>
          {modeState === `edit` && (
            <Button
              variant="outline-secondary"
              className="mr-5"
              onClick={copyEventClick}
            >
              {t("event.copy")}
            </Button>
          )}
          <Stack
            direction="horizontal"
            style={{
              float: "right",
              display: "flex",
              width: modeState === `edit` ? "calc(100% - 125px)" : "100%",
              justifyContent: "flex-end",
            }}
            gap={2}
          >
            <Button variant="primary" onClick={saveEventClick}>
              {t("common.saveChanges")}
            </Button>
            <Button variant="secondary" onClick={onHide}>
              {t("common.close")}
            </Button>
          </Stack>
        </Modal.Footer>
      </Modal>
      <MapModal
        show={showMapModal}
        placeName={placeName}
        address={address}
        onClose={() => {
          setShowMapModel(false);
        }}
      />
    </>
  );
}
